import { isOnline } from '../remote-api';
import { loadReportsOperacionDashboard, loadReportsOperacionRentabilidad } from './reportsOperacionDashboard';
import type { BranchCashSessionReportRow, PosExceptionSummary, ReportsOperacionKpiSources, SalesDailyRow } from '../domain/reportsTypes';
import {
  getReportsDateRange,
  getPreviousHomologousPeriod,
  isSalesTimeBandsApplicable,
  formatReportsLoadError,
  formatIntelligenceLoadError,
  type ReportsCustomRange,
} from '../screens/reportsScreen.utils';
import type { ReportsQueryScope } from '../domain/reportsQueryScope';
import type { DateRangePreset } from '../screens/reportsScreen.constants';
import { getExternalServiceOperationsSummary, getCustomerDaySummary, getIntelligenceKpis, getInventoryDashboard, getPaymentMethods, getPromotionPerformanceByDateRange } from './reportsScreenData';
import type { CashSummary } from '../../cash/domain-api';
import type { ExternalServiceOperationsSummary } from '../../shell/externalServiceRepo';
import type { CustomerDaySummary } from '../../customers/public-api';
import type { IntelligenceKpisResponse } from '../domain/reportsIntelligenceTypes';
import type { PaymentMethodRow, PromotionPerformanceSnapshot, SalesByCashierRow, SalesComparison, SalesSnapshot, SalesByTimeBandRow } from '../../sales/public-api';
import { isDatabaseUnavailableError } from '../../shared/localDb';

export type DashboardData = {
  cashSummary: CashSummary | null;
  todaySalesSnapshot: SalesSnapshot | null;
  periodSalesSnapshot: SalesSnapshot | null;
  previousPeriodSalesSnapshot: SalesSnapshot | null;
  salesDailySeries: SalesDailyRow[] | null;
  posExceptionSummary: PosExceptionSummary | null;
  inventoryDashboard: Awaited<ReturnType<typeof getInventoryDashboard>> | null;
  salesComparison: SalesComparison | null;
  timeBands: SalesByTimeBandRow[];
  salesByCashier: SalesByCashierRow[];
  promotionPerformance: PromotionPerformanceSnapshot | null;
  customerSummary: CustomerDaySummary | null;
  paymentMethods: PaymentMethodRow[];
  externalServiceSummary: ExternalServiceOperationsSummary | null;
  kpiSources: ReportsOperacionKpiSources;
  branchCashSessions: BranchCashSessionReportRow[];
  salesByCashierSource: 'server' | 'device';
  lastDashboardLoadAt: string;
};

export type RentabilidadData = {
  periodRentabilidadKpis: IntelligenceKpisResponse | null;
  previousPeriodRentabilidadKpis: IntelligenceKpisResponse | null;
};

export type LoadDashboardParams = {
  currentBranchId: string;
  userId: string | null | undefined;
  dateRangePreset: DateRangePreset;
  customRangeFrom: Date;
  customRangeTo: Date;
  reportsQueryScope: ReportsQueryScope;
  customerModuleEnabled: boolean;
};

export async function loadDashboardData(params: LoadDashboardParams): Promise<DashboardData> {
  const now = new Date();
  const customParam: ReportsCustomRange | null =
    params.dateRangePreset === 'custom' ? { from: params.customRangeFrom, to: params.customRangeTo } : null;
  const periodRange = getReportsDateRange(now, params.dateRangePreset, customParam);
  const todayRange = getReportsDateRange(now, 'today');
  const { fromIso, toIso, dateForBandsIso } = periodRange;
  const prevPeriodRange = getPreviousHomologousPeriod(now, params.dateRangePreset, periodRange, customParam);
  const bandsApplicable = isSalesTimeBandsApplicable(periodRange);
  const dateForBandsIsoResolved = bandsApplicable ? dateForBandsIso : todayRange.dateForBandsIso;

  const [invDashboard, customer, methods, extSummary] = await Promise.all([
    getInventoryDashboard(params.currentBranchId).catch((error) => {
      if (isDatabaseUnavailableError(error)) {
        return {
          totalProducts: 0,
          belowMin: 0,
          expiringSoon: 0,
          negative: 0,
          expired: 0,
          coveragePercent: 0,
          totalValue: 0,
        };
      }
      throw error;
    }),
    params.customerModuleEnabled
      ? getCustomerDaySummary(params.currentBranchId).catch((error) => {
          if (isDatabaseUnavailableError(error)) {
            return { paymentsCount: 0, paymentsTotal: 0, chargesCount: 0, chargesTotal: 0 };
          }
          throw error;
        })
      : Promise.resolve({ paymentsCount: 0, paymentsTotal: 0, chargesCount: 0, chargesTotal: 0 }),
    getPaymentMethods().catch((error) => {
      if (isDatabaseUnavailableError(error)) {
        const fallback: PaymentMethodRow[] = [
          { id: 'cash', name: 'Efectivo', kind: 'cash', allowsSplit: true, isDigital: false },
          { id: 'transfer', name: 'Transferencia', kind: 'bank', allowsSplit: true, isDigital: true },
          { id: 'mercadopago_qr', name: 'Mercado Pago QR', kind: 'digital', allowsSplit: true, isDigital: true },
        ];
        if (params.customerModuleEnabled) {
          fallback.push({ id: 'fiado', name: 'A cuenta', kind: 'credit', allowsSplit: false, isDigital: false });
        }
        return fallback;
      }
      throw error;
    }),
    getExternalServiceOperationsSummary({
      branchId: params.currentBranchId,
      fromIso,
      toIso,
    }).catch((error) => {
      if (isDatabaseUnavailableError(error)) return { total: 0, count: 0 };
      const msg = error instanceof Error ? error.message : String(error);
      if (msg.includes('no such column')) return { total: 0, count: 0 };
      throw error;
    }),
  ]);

  const todayStart = new Date(now);
  todayStart.setHours(0, 0, 0, 0);
  const todayStartIso = todayStart.toISOString();

  const online = await isOnline();
  const operacion = await loadReportsOperacionDashboard({
    branchId: params.currentBranchId,
    userCashierFilterId: params.userId ?? null,
    online,
    dateRangePresetIsToday: params.dateRangePreset === 'today',
    todayRange: { fromIso: todayRange.fromIso, toIso: todayRange.toIso },
    periodRange: { fromIso, toIso },
    dateForBandsIsoResolved,
    bandsApplicable,
    prevPeriodRange:
      prevPeriodRange != null
        ? { fromIso: prevPeriodRange.fromIso, toIso: prevPeriodRange.toIso }
        : null,
    reportsQueryScope: params.reportsQueryScope,
    todayStartIso,
  });

  const promotionPerf = await getPromotionPerformanceByDateRange(
    params.currentBranchId,
    fromIso,
    toIso,
    params.reportsQueryScope
  ).catch((error: unknown) => {
    // Stub ADR-029: KPI promo aún no tiene RPC — Informes sigue con vacío explícito.
    if (
      error &&
      typeof error === 'object' &&
      'code' in error &&
      (error as { code?: string }).code === 'promotion_performance_unavailable'
    ) {
      return {
        topPromotions: [],
        salesWithDiscountCount: 0,
        salesWithoutDiscountCount: 0,
        salesWithDiscountTotal: 0,
        salesWithoutDiscountTotal: 0,
      };
    }
    throw error;
  });

  return {
    cashSummary: operacion.cashData,
    todaySalesSnapshot: operacion.todaySalesData,
    periodSalesSnapshot: operacion.periodSalesData,
    previousPeriodSalesSnapshot: operacion.previousPeriodSalesData,
    salesDailySeries: operacion.dailySeries,
    posExceptionSummary: operacion.posExceptionSummary,
    inventoryDashboard: invDashboard,
    salesComparison: operacion.comparison,
    timeBands: operacion.bands,
    salesByCashier: operacion.byCashier,
    promotionPerformance: promotionPerf,
    customerSummary: customer,
    paymentMethods: methods,
    externalServiceSummary: extSummary,
    kpiSources: operacion.kpiSources,
    branchCashSessions: operacion.branchCashSessions,
    salesByCashierSource: operacion.salesByCashierSource,
    lastDashboardLoadAt: new Date().toISOString(),
  };
}

export async function loadRentabilidadData(params: {
  currentBranchId: string;
  dateRangePreset: DateRangePreset;
  customRangeFrom: Date;
  customRangeTo: Date;
  reportsQueryScope: ReportsQueryScope;
}): Promise<RentabilidadData> {
  const now = new Date();
  const customParam: ReportsCustomRange | null =
    params.dateRangePreset === 'custom' ? { from: params.customRangeFrom, to: params.customRangeTo } : null;
  const periodRange = getReportsDateRange(now, params.dateRangePreset, customParam);
  const prevPeriodRange = getPreviousHomologousPeriod(now, params.dateRangePreset, periodRange, customParam);

  const online = await isOnline();
  const rentabilidad = await loadReportsOperacionRentabilidad({
    branchId: params.currentBranchId,
    fromIso: periodRange.fromIso,
    toIso: periodRange.toIso,
    prevPeriodRange:
      prevPeriodRange != null
        ? { fromIso: prevPeriodRange.fromIso, toIso: prevPeriodRange.toIso }
        : null,
    reportsQueryScope: params.reportsQueryScope,
    online,
  });

  return {
    periodRentabilidadKpis: rentabilidad.periodRentabilidadKpis,
    previousPeriodRentabilidadKpis: rentabilidad.previousPeriodRentabilidadKpis,
  };
}

export async function loadIntelligenceData(params: {
  currentBranchId: string;
  dateRangePreset: DateRangePreset;
  customRangeFrom: Date;
  customRangeTo: Date;
  reportsQueryScope: ReportsQueryScope;
  periodRentabilidadKpis: IntelligenceKpisResponse | null;
}): Promise<IntelligenceKpisResponse> {
  if (!(await isOnline())) {
    throw new Error(
      'Los datos de cierres necesitan internet. Ventas y Operación pueden usar lo de esta compu.',
    );
  }

  const range = getReportsDateRange(
    new Date(),
    params.dateRangePreset,
    params.dateRangePreset === 'custom' ? { from: params.customRangeFrom, to: params.customRangeTo } : null
  );

  // periodRentabilidadKpis ya se pide con el mismo queryScope (loadRentabilidadData).
  if (
    params.periodRentabilidadKpis &&
    params.periodRentabilidadKpis.range.fromIso === range.fromIso &&
    params.periodRentabilidadKpis.range.toIso === range.toIso
  ) {
    return params.periodRentabilidadKpis;
  }

  return getIntelligenceKpis({
    branchId: params.currentBranchId,
    fromIso: range.fromIso,
    toIso: range.toIso,
    queryScope: params.reportsQueryScope,
  });
}

export { formatReportsLoadError, formatIntelligenceLoadError };
